import {
  FETCH_SURVEY_PENDING,
  FETCH_SURVEY_SUCCESS,
  FETCH_SURVEY_FAIL,
  GOTO_NEXT_QUESTION,
  GOTO_PREV_QUESTION,
  SUBMIT_SURVEY_PENDING,
  SUBMIT_SURVEY_SUCCESS,
  SUBMIT_SURVEY_FAIL,
  FETCH_SURVEY_RESULT_SUCCESS,
  FETCH_SURVEY_RESULT_PENDING,
  FETCH_SURVEY_RESULT_FAIL,
  GOTO_QUESTION,
  GOTO_SUBMIT_PAGE,
  GOTO_SURVEY_PAGE,
} from '~/constants/action_types';
import { normalizedQuestions } from '~/helpers/question_helper';
import { normalizedAnswers, isValidAnswer } from '~/helpers/answer_helper';
import { getSelectedLanguageISO, normalizeLanguages } from '~/helpers/languages_helper';
import { normalizeResults } from '~/helpers/result_helper';
import { hasMatchedCondition } from '~/helpers/logic_helper';
import { setLanguageISO, setLanguages } from './languages_actions';
import { DEFAULT_LANG } from '~/constants/globals';

// NOTE: `hashCode` can be either a survey-code or a preview-code. The only way
// to tell the difference is based on the `isPreview` attribute.
export const fetchSurvey =
  (hashCode, previewEmployeeId = null) =>
  async (dispatch, getState, services) => {
    dispatch(fetchSurveyPending());

    try {
      const state = getState();
      const languageISOCode = state.languages.languageISO;
      const selectedLanguage = state.languages.selectedLanguage;
      const data = await services.fetchEmployeeSurvey(hashCode, previewEmployeeId);

      if (!data) throw Error(`No survey data found with hash-code ${hashCode}`);

      dispatch(fetchSurveySuccess({ hashCode, ...data }));

      const languages = normalizeLanguages(data.languages);

      dispatch(setLanguages(languages));

      if (!selectedLanguage) {
        const employeePreferredLanguage =
          languages.find((lang) => Number(lang.id) === data.employeePreferredLanguageId) || {};
        const preferredISOCode = languageISOCode || employeePreferredLanguage.langISOCode;
        const selectedISOCode = getSelectedLanguageISO(data.languages, preferredISOCode, DEFAULT_LANG);
        dispatch(setLanguageISO(selectedISOCode));
      }
    } catch (err) {
      dispatch(fetchSurveyFail(err.message));
    }
  };

export const fetchSurveyResult = (hashCode) => async (dispatch, getState, services) => {
  dispatch(fetchSurveyResultPending());
  const state = getState();

  try {
    const data = await services.fetchEmployeeSurveyResult(hashCode, state.survey.companyId);
    if (!data) throw Error(`No survey result data found with hash-code ${hashCode}`);

    const results = normalizeResults(state.questions, data, state.languages.languageISO);
    dispatch(fetchSurveyResultSuccess(results));
  } catch (err) {
    dispatch(fetchSurveyResultFail(err.message));
  }
};

const fetchSurveyPending = () => ({
  type: FETCH_SURVEY_PENDING,
});

// Using the question format generated in `question_helper`, append the
// `questionType` to each answer for easy reference.
const answersWithQuestionType = (questions, answers) =>
  answers.reduce((rawAnswersWithType, answer) => {
    const question = questions.find((q) => q.id === answer.questionId);

    // If cannot find matching question, skip this answer.
    if (!question) {
      return rawAnswersWithType;
    }

    // Append found `question.type` to answer.
    const updatedAnswer = {
      ...answer,
      questionType: question.type,
    };

    return [...rawAnswersWithType, updatedAnswer];
  }, []);

const fetchSurveySuccess = (data) => {
  const questionLogics = data.questionLogics || [];
  const allQuestions = normalizedQuestions(data.questions, questionLogics);

  // make sure at lease one of the question's display condition exists
  const questionIds = allQuestions.map((question) => question.id);
  const questions = allQuestions.filter((question) => {
    if (!question.displayConditions || question.displayConditions.length < 1) return true;
    const hasDisplayCondition = question.displayConditions.some((condition) =>
      questionIds.includes(condition.mainSurveyElementId),
    );
    return hasDisplayCondition;
  });

  const answers =
    !data.answers || !Array.isArray(data.answers)
      ? {}
      : normalizedAnswers(answersWithQuestionType(questions, data.answers));

  // Edge case protection, if user has invalid answer in any case, land them on the invalid answer
  const invalidAnswerIndex = questions.findIndex((question) => {
    const isAlwaysVisible = !question.displayConditions || question.displayConditions <= 0;
    const isVisibleByCondition = hasMatchedCondition(question.displayConditions, answers);
    const isVisible = isAlwaysVisible || isVisibleByCondition;
    return isVisible && !isValidAnswer(question, answers[question.id]);
  });

  let showSubmitPage = false;
  // Must find last answered rather than find first unanswered here, as message type do not have an answer
  const lastAnsweredReversedIndex = [...questions].reverse().findIndex((question) => answers[question.id]);
  let currentQuestionIndex;
  switch (lastAnsweredReversedIndex) {
    // No answer yet
    case -1:
      currentQuestionIndex = 0;
      break;
    // Answered all questions
    case 0:
      currentQuestionIndex = questions.length - 1;
      showSubmitPage = true;
      break;
    default: {
      const lastAnsweredIndex = questions.length - lastAnsweredReversedIndex - 1;
      const nextVisibleIndex = questions.findIndex((q, index) => {
        if (index <= lastAnsweredIndex) return false;
        if (!q.displayConditions || q.displayConditions <= 0) return true;
        return hasMatchedCondition(q.displayConditions, answers);
      });
      if (nextVisibleIndex === -1 && invalidAnswerIndex === -1) {
        // No more visible question and all required question answered
        showSubmitPage = true;
      }
      currentQuestionIndex = nextVisibleIndex !== -1 ? nextVisibleIndex : lastAnsweredIndex;
      break;
    }
  }

  if (invalidAnswerIndex !== -1 && invalidAnswerIndex < currentQuestionIndex) {
    currentQuestionIndex = invalidAnswerIndex;
  }

  return {
    type: FETCH_SURVEY_SUCCESS,
    surveyCode: data.hashCode,
    id: data.id,
    name: data.name,
    status: data.status.name,
    isPreview: data.isPreview,
    canViewResults: data.showResults,
    companyId: data.companyId,
    submitted: data.submitted,
    showSubmitPage: questions.length ? showSubmitPage : true,
    expired: data.expired,
    currentQuestionIndex,
    questions,
    answers,
    anonymityThreshold: data.anonymityThreshold,
  };
};

const fetchSurveyFail = (error) => ({
  type: FETCH_SURVEY_FAIL,
  error,
});

const fetchSurveyResultPending = () => ({
  type: FETCH_SURVEY_RESULT_PENDING,
});

const fetchSurveyResultSuccess = (data) => ({
  type: FETCH_SURVEY_RESULT_SUCCESS,
  results: data,
});

const fetchSurveyResultFail = (error) => ({
  type: FETCH_SURVEY_RESULT_FAIL,
  error,
});

export const gotoQuestion = (questionId) => (dispatch, getState) => {
  const state = getState();
  const targetIndex = state.questions.findIndex((q) => q.id === questionId);

  dispatch({
    type: GOTO_QUESTION,
    currentQuestionIndex: targetIndex,
  });
};

export const gotoSubmitPage = () => (dispatch) => {
  dispatch({
    type: GOTO_SUBMIT_PAGE,
  });
};

export const gotoSurveyPage = () => (dispatch) => {
  dispatch({
    type: GOTO_SURVEY_PAGE,
  });
};

export const gotoNextQuestion = (currentQuestionId) => (dispatch, getState) => {
  const state = getState();
  const currentIndex = state.questions.findIndex((q) => q.id === currentQuestionId);
  const lastQuestionIndex = state.questions.length - 1;
  if (currentIndex >= lastQuestionIndex) {
    dispatch({
      type: GOTO_NEXT_QUESTION,
      lastQuestionIndex,
    });
    return;
  }

  const nextQuestionIndex = state.questions.findIndex((q, index) => {
    if (index <= currentIndex) return false;
    if (!q.displayConditions || q.displayConditions <= 0) return true;
    return hasMatchedCondition(q.displayConditions, state.answers);
  });

  if (nextQuestionIndex === -1) return;

  dispatch({
    type: GOTO_NEXT_QUESTION,
    nextQuestionIndex,
  });
};

export const gotoPrevQuestion = (currentQuestionId) => (dispatch, getState) => {
  const state = getState();
  const currentIndex = state.questions.findIndex((q) => q.id === currentQuestionId);
  if (currentIndex <= 0) {
    dispatch({
      type: GOTO_PREV_QUESTION,
      prevQuestionIndex: 0,
    });
    return;
  }

  const reversedCurrentIndex = state.questions.length - currentIndex - 1;
  const prevQuestion = [...state.questions].reverse().find((q, index) => {
    if (index <= reversedCurrentIndex) return false;
    if (!q.displayConditions || q.displayConditions <= 0) return true;
    return hasMatchedCondition(q.displayConditions, state.answers);
  });

  const prevQuestionIndex = state.questions.findIndex((q) => q.id === prevQuestion.id);

  dispatch({
    type: GOTO_PREV_QUESTION,
    prevQuestionIndex,
  });
};

export const submitSurvey = (surveyCode) => async (dispatch, getState, services) => {
  dispatch(submitSurveyPending());

  try {
    const state = getState();

    if (state.survey.isPreview) {
      dispatch(submitSurveySuccess('Preview Mode'));
    } else {
      const res = await services.submitSurvey(surveyCode);
      if (res.success) {
        dispatch(submitSurveySuccess(res));
      } else {
        dispatch(submitSurveyFail(res.error?.message, res.expired));
      }
    }
  } catch (err) {
    dispatch(submitSurveyFail(err.message));
  }
};

const submitSurveyPending = () => ({
  type: SUBMIT_SURVEY_PENDING,
});

const submitSurveySuccess = (res) => ({
  type: SUBMIT_SURVEY_SUCCESS,
  response: res,
});

const submitSurveyFail = (error, expired = false) => ({
  type: SUBMIT_SURVEY_FAIL,
  error,
  expired,
});
